import { Box, Typography } from '@mui/material';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import DoseProperties from '~/components/Properties/DoseProperties';
import { useObjectDetailsDialog } from '~/components/Properties/ObjectDetailsDialog';
import ObjectInfoButton, {
    ObjectRow,
} from '~/components/Properties/ObjectInfoButton';
import { PropertiesActions } from '~/components/Properties/PropertiesPanel';
import PropertiesTooltip from '~/components/Properties/PropertiesTooltip';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { formatDateString } from '~/utils/date';

interface DoseItemProps {
    dose: DoseMatrix;
    label: string;
    onDelete?: () => void;
}

export default function DoseItem({ dose, label, onDelete }: DoseItemProps) {
    // Series date is plumbed through DicomDose from the original DICOM
    // SeriesDate (0008,0021) tag in YYYYMMDD format. We render a
    // human-readable representation; if parsing fails the value is
    // simply omitted to avoid showing garbled text.
    const formattedDate = dose.dicomDose.seriesDate
        ? formatDateString(dose.dicomDose.seriesDate)
        : undefined;
    const dialog = useObjectDetailsDialog();

    const content = (
        <PropertiesTooltip
            title={
                <DoseProperties
                    dose={dose}
                    onViewDicom={() => dialog.open('tags')}
                    onDelete={onDelete}
                />
            }
        >
            <Box display="grid" width="100%" overflow="hidden">
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    width="100%"
                    overflow="hidden"
                >
                    <Typography fontSize={12} noWrap flex={1} minWidth={0}>
                        <SensitiveInfoTypography>
                            {label}
                        </SensitiveInfoTypography>
                    </Typography>
                    <Typography
                        fontSize={10}
                        color="text.secondary"
                        whiteSpace="nowrap"
                        flexShrink={0}
                    >
                        {dose.doseMaxGy.toFixed(2)} Gy
                    </Typography>
                </Box>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={2}
                    width="100%"
                    overflow="hidden"
                >
                    {formattedDate && (
                        <Typography
                            fontSize={10}
                            color="text.secondary"
                            noWrap
                            sx={{ minWidth: 0 }}
                        >
                            <SensitiveInfoTypography>
                                {formattedDate}
                            </SensitiveInfoTypography>
                        </Typography>
                    )}
                    {dose.doseSummationType && (
                        <Typography
                            fontSize={10}
                            color="text.secondary"
                            noWrap
                            flexShrink={0}
                        >
                            {dose.doseSummationType}
                        </Typography>
                    )}
                </Box>
            </Box>
        </PropertiesTooltip>
    );

    return (
        <ObjectRow
            info={
                <ObjectInfoButton
                    titleId="dose"
                    name={label}
                    dialog={dialog}
                    actions={<PropertiesActions onDelete={onDelete} />}
                    dataset={dose.dicomDose.datasetWrapper}
                >
                    <DoseProperties dose={dose} />
                </ObjectInfoButton>
            }
        >
            {content}
        </ObjectRow>
    );
}
